import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import LandingPage from '@/pages/LandingPage';
import '@/App.css';

// Chargées en différé : seule LandingPage doit être dans le bundle initial de "/" pour le LCP
// mobile (le reste — dont recharts/jspdf via ReportsPage/DashboardPage — n'a rien à faire dans
// le bundle téléchargé et exécuté avant même d'afficher la page d'accueil).
const Layout = lazy(() => import('@/components/Layout'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ContractsPage = lazy(() => import('@/pages/ContractsPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const AlertsPage = lazy(() => import('@/pages/AlertsPage'));
const SuppliersPage = lazy(() => import('@/pages/SuppliersPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const InvitationPage = lazy(() => import('@/pages/InvitationPage'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const CGVPage = lazy(() => import('@/pages/legal/CGVPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const RGPDPage = lazy(() => import('@/pages/legal/RGPDPage'));
const MentionsLegalesPage = lazy(() => import('@/pages/legal/MentionsLegalesPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center" data-testid="loading-screen">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-[#00D4AA] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-[#6B7A99]">Chargement...</p>
      </div>
    </div>
  );
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function PublicOnlyRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user && user !== false) return <Navigate to="/app" replace />;
  return <Outlet />;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (!urlParams.get('fromSignup')) return <Navigate to="/app" replace />;
  return <OnboardingPage />;
}

function AdminDashboardRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/admin-gc2026" replace />;
  if (user.role !== 'superadmin') return <Navigate to="/app" replace />;
  return <AdminDashboardPage />;
}

function App() {
  useEffect(() => {
    // Ces balises meta/title statiques (index.html) ne servent que de filet de sécurité pour
    // les robots qui ne lisent que le HTML brut (Facebook, LinkedIn, X, Slack). Dès que le JS
    // s'exécute, chaque page les remplace via Helmet — on retire donc les originales une fois
    // pour éviter un doublon (deux <title>, deux <meta name="description">, etc.) dans le head.
    document.querySelectorAll('[data-rh-fallback]').forEach((el) => el.remove());
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
        <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Landing — always public, no auth redirect */}
          <Route path="/" element={<LandingPage />} />

          {/* Auth pages — redirect to /app if already logged in */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
          </Route>

          {/* App — protected */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route path="/app" element={<DashboardPage />} />
              <Route path="/contrats" element={<ContractsPage />} />
              <Route path="/calendrier" element={<CalendarPage />} />
              <Route path="/alertes" element={<AlertsPage />} />
              <Route path="/fournisseurs" element={<SuppliersPage />} />
              <Route path="/rapports" element={<ReportsPage />} />
              <Route path="/parametres" element={<SettingsPage />} />
            </Route>
          </Route>

          {/* Onboarding — protected, only reachable from signup flow */}
          <Route path="/onboarding" element={<OnboardingRoute />} />

          {/* Email verification — always public */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Password reset — always public */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Blog — always public */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Legal pages — always public */}
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/rgpd" element={<RGPDPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />

          {/* Invitation acceptance — always public, no auth redirect */}
          <Route path="/invitation" element={<InvitationPage />} />

          {/* Admin — standalone, no Layout */}
          <Route path="/admin-gc2026" element={<AdminLoginPage />} />
          <Route path="/admin-gc2026/dashboard" element={<AdminDashboardRoute />} />

          {/* Backward-compat redirects */}
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/dashboard" element={<Navigate to="/app" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        <Toaster position="bottom-right" richColors closeButton />
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;